/**
 * Clearing READ endpoints — request schemas + wire types.
 *
 * Unlike the write schemas in `clearing.ts` (Pattern C — SECURITY DEFINER
 * RPCs behind verify_backend_token), these serve plain RLS-scoped reads:
 * the Hono handlers run the queries through the per-request user client, so
 * org RLS does the authorization. Routing reads through the backend keeps
 * the clearing data-access layer in one place (see docs/BACKEND_API.md →
 * "Read endpoints").
 *
 * The wire interfaces below are shared by the API handlers (response
 * construction) and the web hooks (unwrap<T> targets) so the two sides
 * cannot drift.
 */
import { z } from "zod";
import { calendarDateSchema } from "./dateWindow.js";
export const importStatusEnum = z.enum([
    "pending",
    "matched",
    "applied",
    "skipped",
]);
/**
 * Direction of a bank line as the statement states it, already sign-resolved
 * at import (`resolveImportIndicator`, incl. the credit-card flip):
 * `credit` = money IN, `debit` = money OUT. Mirrors the inline union on
 * `AccountingImportRecord.credit_debit_indicator` / the DB column, and is the
 * value the candidate endpoints translate into a bank-leg direction:
 * money in  → the journal DEBITS the bank account,
 * money out → the journal CREDITS it.
 */
export const creditDebitIndicatorEnum = z.enum(["credit", "debit"]);
/**
 * Query params for GET /orgs/:orgId/imports/index — the slim bank-feed index.
 *
 * Only ONE dimension, unlike the fat list above: there is no `status` filter.
 * The index endpoint always serves the COMPLETE feed for the account and the
 * grid filters status client-side (the slim-index contract — see
 * docs/INDEX_GRID_PATTERN.md), so a server-side status narrowing would only
 * fragment the cache for no gain.
 */
export const accountingImportIndexQuerySchema = z.object({
    bankAccountId: z.string().uuid().optional(),
});
/** Query params for GET /orgs/:orgId/imports/stats. */
export const importStatsQuerySchema = z.object({
    bankAccountId: z.string().uuid().optional(),
});
/**
 * Query params for GET /orgs/:orgId/cleared-balances.
 *
 * Deliberately a clone of `importStatsQuerySchema` rather than a reuse: the
 * two endpoints answer different questions over the same feed (import status
 * counts vs. the cleared/uncleared balance bridge) and are free to diverge —
 * e.g. an "as of" date on this one — without dragging the other with them.
 * Absent `bankAccountId` means "every feed account in the org" (the account
 * strip); present means the single pinned account (the Overview tab).
 */
export const clearedBalancesQuerySchema = z.object({
    bankAccountId: z.string().uuid().optional(),
});
/**
 * Body for POST /orgs/:orgId/match-candidates.
 *
 * A POST-shaped read: `amounts` is the union of every visible pending bank
 * line's amount — potentially hundreds of values, which would blow the URL
 * budget as a GET query string. The 1000 cap mirrors the list fetch page
 * size (the amounts derive from at most one full feed's pending rows).
 *
 * `amounts_in` / `amounts_out` are the same union SPLIT by the row's
 * `credit_debit_indicator` — money in (`credit`) vs money out (`debit`) — so
 * the handler can filter candidates on the DIRECTION of their bank leg, not
 * just its magnitude. Without it the endpoint offers a journal whose bank leg
 * runs the opposite way, which the Phase 2 net guard on
 * `link_to_accounting_import` / `commit_import_split` then refuses (the
 * production +25,000 shape). Both are OPTIONAL and their `.min(1)` is
 * conditional on presence for a reason: a browser tab loaded before this
 * release sends neither key, and must keep getting today's undirected result
 * rather than a 400 — the handler skips the direction filter when both are
 * absent. Ship the API before the web app; tighten to required next release.
 * When both are absent the handler filters on a bank leg of EITHER side
 * sized like one of `amounts`. Candidates are never filtered on
 * `total_amount` — a statement line settles the journal's bank leg, which
 * is not its total on a multi-debit journal (a sales-channel payout).
 */
export const matchCandidatesRequestSchema = z
    .object({
    bank_account_id: z.string().uuid(),
    amounts: z.array(z.number().finite()).min(1).max(1000),
    amounts_in: z.array(z.number().finite()).max(1000).optional(),
    amounts_out: z.array(z.number().finite()).max(1000).optional(),
    // `calendarDateSchema`, not a bare yyyy-MM-dd regex: the handler now sends
    // these bounds straight down as plain `.gte`/`.lte` on a `date` column, so
    // a regex-valid non-day (`2026-13-05`, `2026-02-31`) would reach Postgres
    // as a cast error — a 500 for what is plainly a bad request. The refine
    // keeps it a 400.
    date_min: calendarDateSchema,
    date_max: calendarDateSchema,
})
    .strict();
/**
 * Query params for GET /orgs/:orgId/allocation-candidates.
 *
 * Bounds are `calendarDateSchema` for the same reason as /match-candidates —
 * the two candidate surfaces share a window contract and must not drift.
 */
export const allocationCandidatesQuerySchema = z.object({
    amount: z.coerce.number().finite(),
    dateMin: calendarDateSchema,
    dateMax: calendarDateSchema,
    bankAccountId: z.string().uuid(),
    /**
     * The import row's direction. Present → the handler additionally requires
     * the candidate's bank leg to run that way (`credit` → `entries.debit_amount
     * = amount`, `debit` → `entries.credit_amount = amount`), so the Match tab
     * cannot offer a journal the Phase 2 net guard would refuse. OPTIONAL for
     * one release: a stale browser tab omits it and keeps today's undirected
     * (magnitude-only) list rather than 400ing. Tighten to required next release.
     */
    creditDebitIndicator: creditDebitIndicatorEnum.optional(),
});
/** Query params for GET /orgs/:orgId/imports/:id/posting-details. */
export const postingDetailsQuerySchema = z.object({
    residualTransactionId: z.string().uuid().optional(),
});
/**
 * Body for POST /orgs/:orgId/category-recommendations.
 *
 * A POST-shaped read (like /match-candidates): `signals` is one entry per
 * visible pending bank line, carrying the free-text attributes the
 * recommend_import_categories RPC keys on. `description` is trimmed/truncated
 * client-side to keep the body bounded on hundred-row pages. The 500 cap is
 * below the 1000 used by /match-candidates — a page rarely shows that many
 * pending rows, and each signal here is heavier than a bare amount.
 */
export const categoryRecommendationSignalSchema = z
    .object({
    import_id: z.string().uuid(),
    related_party_account: z.string().max(256).nullable().optional(),
    related_party_name: z.string().max(512).nullable().optional(),
    // Bounded so tier-3 similarity() cost stays fixed per signal — the web
    // hook already truncates to 200; this is the server-side backstop.
    description: z.string().max(512).nullable().optional(),
    signed_amount: z.number().finite(),
})
    .strict();
export const categoryRecommendationsRequestSchema = z
    .object({
    signals: z.array(categoryRecommendationSignalSchema).min(1).max(500),
})
    .strict();
// ---------- cross-account import-match warning (bank import Configure step) ----------
/**
 * Request body for POST /clearing/orgs/:orgId/cross-account-import-matches.
 *
 * A POST-shaped read: `entry_references` is the union of the parsed
 * statement's entry references (can be hundreds). The 2000 cap is required —
 * supabase-js `.in()` still serializes to a PostgREST GET filter server-side,
 * so an unbounded list would blow the URL-length limit.
 */
export const crossAccountImportMatchesSchema = z
    .object({
    bank_account_id: z.string().uuid(),
    entry_references: z.array(z.string()).min(1).max(2000),
})
    .strict();
// ---------- loose duplicate detection (CSV Review step + Enable Banking sync) ----
/**
 * Hard cap on the probe rows one loose-duplicate check may carry.
 *
 * Same budget as `crossAccountImportMatchesSchema.entry_references`: the
 * request is a POST, but the read it drives fans out into PostgREST `.in()`
 * GET filters, and the pairing below is O(rows) per key. A statement bigger
 * than this is a pathological upload, not a real bank export.
 */
export const LOOSE_DUPLICATE_CHECK_MAX_ROWS = 2000;
/**
 * One parsed-but-not-yet-inserted statement line, as the loose duplicate check
 * probes it — from the CSV wizard's Review step over the wire, or built
 * in-process by the Enable Banking sync (`toLooseProbe`) for every chunk it is
 * about to insert.
 *
 * `credit_debit_indicator` is POST-flip (`resolveImportIndicator`, incl. the
 * credit-card flip), so it lines up with the stored column on
 * `accounting_imports` without the caller re-deriving anything.
 *
 * `reference` is the statement's own reference field where the export carries
 * one; `bank_transaction_id` is the optional CSV "bank transaction id" column
 * (Rabobank `Volgnr` and friends), which is what makes CSV dedup exact. Both
 * are optional because most exports carry neither.
 *
 * The four evidence fields feed the cross-source rules in `looseDuplicate.ts`
 * (an Enable Banking line against the CAMT/CSV copy already in the feed, where
 * no exact key can bridge the two id spaces): `related_party_account` is the
 * counterparty IBAN (rule 3, a veto when both sides carry one and they
 * differ), `end_to_end_id` is the SEPA end-to-end id (rule 4), and
 * `description` / `remittance_info` are the text one side's e2e is looked for
 * in (rule 4's token scan) and the containment tie-break reads (rule 5). All
 * four are `.nullish()` ON PURPOSE: this schema is `.strict()`, and a stale
 * web tab loaded before this release omits the keys entirely — `.nullable()`
 * alone would make them REQUIRED and 400 every Review check from that tab.
 * The CSV wizard sends `related_party_account` only; the sync fills all four.
 * Length caps mirror the wire's other string fields (the DB columns are
 * VARCHAR(255) / TEXT); the sync path builds the probe in-process and is not
 * bound by them.
 */
export const looseDuplicateProbeRowSchema = z
    .object({
    client_id: z.string().min(1),
    booking_date: calendarDateSchema,
    amount: z.number().nonnegative(),
    credit_debit_indicator: creditDebitIndicatorEnum,
    reference: z.string().max(255).nullish(),
    bank_transaction_id: z.string().max(255).nullish(),
    related_party_account: z.string().max(255).nullish(),
    end_to_end_id: z.string().max(255).nullish(),
    description: z.string().max(2000).nullish(),
    remittance_info: z.string().max(2000).nullish(),
})
    .strict();
/**
 * Request body for POST /bank-import/orgs/:orgId/loose-duplicate-matches.
 *
 * ORDER IS PART OF THE CONTRACT: `rows` must arrive in file order (ascending
 * `line_index`). `pairLooseDuplicates` consumes each key's existing rows in
 * that order, so the file's FIRST copy of a repeated line is the one flagged.
 */
export const looseDuplicateMatchesRequestSchema = z
    .object({
    bank_account_id: z.string().uuid(),
    rows: z
        .array(looseDuplicateProbeRowSchema)
        .min(1)
        .max(LOOSE_DUPLICATE_CHECK_MAX_ROWS),
})
    .strict();
// ---------- duplicate suggestions (the feed grid's "Duplicate of …" chip) ----
/**
 * Max pending ids one `/duplicate-suggestions` request may carry. Same figure
 * as `categoryRecommendationsRequestSchema`'s 500 (the other per-pending-row
 * lookahead the grid runs) — the web hook chunks a bigger pending set into
 * requests of this size. Only CONSUMPTION is per request (an original absorbs
 * one copy per response), so a chunk split can over-suggest in a corner case;
 * the candidate set itself is read by day/amount and is never split.
 */
export const DUPLICATE_SUGGESTIONS_MAX_IDS = 500;
/**
 * Body for POST /orgs/:orgId/duplicate-suggestions (mounted in clearing.ts
 * beside /match-candidates and /category-recommendations — a POST-shaped read
 * like both).
 *
 * Only IDS travel: the SERVER reads the pending rows by id under RLS and
 * builds the probes itself. The slim index row lacks `end_to_end_id` /
 * `import_batch_id`, so a client-built probe would be evidence-stripped (every
 * pair `key_only`) and could not honour the same-batch exclusion — and a
 * client must not be able to hand the engine a probe it invented. Ids not
 * pending (a stale tab), not on this account or not visible are dropped
 * silently, never a 400.
 */
export const duplicateSuggestionsRequestSchema = z
    .object({
    bank_account_id: z.string().uuid(),
    import_ids: z
        .array(z.string().uuid())
        .min(1)
        .max(DUPLICATE_SUGGESTIONS_MAX_IDS),
})
    .strict();
//# sourceMappingURL=clearingReads.js.map